import { api } from './api';
import {
  auth as authHelpers,
  storage
} from '../helpers';
import { toastr } from 'react-redux-toastr';
import {
  jira as jiraActions,
  modal as modalActions,
  notifications as notificationsActions,
} from './index';

/**
 * Returns a Basic auth compatible encoded string from given credentials
 * @param {obj} credentials : containing username and password to encode
 * @returns Base64 encoded string for Basic auth
 */
const formatAuthKey = (credentials) => {
  let payload;
  try {
    payload = window.btoa(credentials.username + ':' + credentials.password);
  } catch (error) {
    toastr.warning(
      'Login Failed (only latin characters are supported values in login credentials)',
      error, { timeOut: 0 });
    return '';
  }
  const payloadEncoded = encodeURI(payload);
  const authKey = payloadEncoded === 'Og==' ? '' : payloadEncoded;
  return authKey;
};

/**
 * Attempts to authenticate given credentials with the backend
 * and carries out relevant side effects on success or failure.
 * @param {obj} credentials : containing username and password to encode
 */
export const performSignOn = (credentials, redirectFunc) => async (dispatch) => {
  const authKey = formatAuthKey(credentials);

  try {
    const authToken = await api.auth.authenticate(authKey);
    authHelpers.storePayloadData(authToken);
    // front end authentication process considered complete at this point
    toastr.info('Logged In', 'Successfully Logged in', { timeOut: 0 });
    postSignOnActions(dispatch, redirectFunc);
  } catch (errorString) {
    // temp hardcoded check of 401 response string
    // until error is refactored to include full response object STS-4589
    if (errorString === 'Could not log in with given credentials.') {
      authHelpers.removeAuthentication();
    }
    toastr.warning('Login Failed', errorString, { timeOut: 0 });
  }
};

/**
 * Attempts to fetch some useful data that is auth dependent but not necessarily
 * required immediately.
 * @param {*} dispatch - The dispatch used to dispatch actions
 */
export const postSignOnActions = (dispatch, redirectFunc) => {
  if (!dispatch) {
    return;
  }

  // Try to be proactive for the user and fetch scans.
  // For most sign on actions the user won't even be working with these but this
  // can also have side-effect of helping keep SCAN cache fresh by overall user pool
  const authToken = localStorage.getItem(storage.AUTH_TOKEN);
  dispatch(jiraActions.fetchJiraScans(authToken, 50));

  const userEmail = localStorage.getItem(storage.EMAIL_KEY);
  dispatch(notificationsActions.fetchNotificationRules());
  dispatch(notificationsActions.fetchNotificationRequests(userEmail));
  if (redirectFunc)
    dispatch(redirectFunc());
  window.location.reload();
};

/**
 * Attempts to authenticate using the single sign-on identity the AWS load balancer
 * attaches to requests. Resolves false where there is none (e.g. on-prem), so the
 * caller can fall back to the credentials form.
 * @returns Promise resolving true if a dashboard token was stored
 */
export const performSsoSignOn = () => async () => {
  try {
    const authToken = await api.auth.authenticate('');
    authHelpers.storePayloadData(authToken);
    return authHelpers.checkAuthenticated();
  } catch (error) {
    return false;
  }
};

/**
 * On page load, signs in through single sign-on once per tab session if not
 * already authenticated. Reloads on success so components pick up the new login.
 */
export const performStartupSsoSignOn = () => async (dispatch) => {
  if (authHelpers.checkAuthenticated()) {
    sessionStorage.removeItem(storage.SSO_ATTEMPTED_KEY);
    return;
  }

  // guard against a reload loop if a stored token is still considered unauthenticated
  if (sessionStorage.getItem(storage.SSO_ATTEMPTED_KEY)) {
    return;
  }
  sessionStorage.setItem(storage.SSO_ATTEMPTED_KEY, 'true');

  if (await dispatch(performSsoSignOn())) {
    window.location.reload();
  }
};

/**
 * Signs in through single sign-on when available, otherwise opens the login modal.
 * @param {function} redirectFunc : optional action creator to dispatch after signing in
 */
export const signInOrShowModal = (redirectFunc) => async (dispatch) => {
  if (await dispatch(performSsoSignOn())) {
    postSignOnActions(dispatch, redirectFunc);
    return;
  }

  dispatch(modalActions.showJiraLoginModal(true, redirectFunc));
};

/**
 * Logs out of the dashboard, and out of single sign-on when it was used.
 */
export const performSignOut = () => async () => {
  let logoutUrl = '';
  try {
    logoutUrl = await api.auth.logout();
  } catch (error) {
    // on-prem or older backends: clearing local storage is all there is to do
  }

  authHelpers.removeAuthentication();
  if (logoutUrl) {
    window.location.assign(logoutUrl);
  } else {
    window.location.reload();
  }
};

/**
 * Checks an error from rejected ajax promise for
 * invalid auth conditions and if satisfied will log out the user
 * @param {any} ajaxRejectError : return value of an ajax reject
 */
export const authErrorFunc = (ajaxRejectError) => {
  if (ajaxRejectError === 'Forbidden') {
    authHelpers.removeAuthentication();
  }
};

export const testingExports = { formatAuthKey };


// WEBPACK FOOTER //
// src/actions/auth.js